import { CSSResult, html, LitElement, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { consume } from '@lit/context';
import styles from './wishlist-brand-tile.scss?inline';
import { Messages, WishlistBrandListItem } from '../../../@types/wishlist.js';
import { WishlistAppConfig, wishlistAppConfigContext } from '../../../scripts/context/appConfigContext.js';
import { i18nContext } from '../../../scripts/context/i18nContext.js';
import { trackBrandlistListClick, trackBrandlistListView } from '../../../scripts/service/cube-tracking-layer.js';
import '../wishlist-brand-remove-button/wishlist-brand-remove-button.js';

@customElement('ents-wishlist-brand-tile')
export class WishlistBrandTileElement extends LitElement {
  @consume({ context: wishlistAppConfigContext })
  private wishlistAppConfig!: WishlistAppConfig;

  @consume({ context: i18nContext })
  private messages!: Messages;

  @property({ type: Object }) brand!: WishlistBrandListItem;

  @property({ type: Number }) position: number = 0;

  @property({ type: Number }) totalBrands: number = 0;

  @property({ type: Boolean }) loggedIn: boolean = false;

  trackingObserver: IntersectionObserver | null = null;

  private hasTrackedView = false;

  private trackingDebounceTimer?: number;

  static get styles(): CSSResult {
    return unsafeCSS(styles);
  }

  override connectedCallback() {
    super.connectedCallback();
    if (!this.hasTrackedView) {
      this.trackingObserver?.observe(this);
    }
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.trackingObserver?.unobserve(this);
    clearTimeout(this.trackingDebounceTimer);
  }

  public trackViewDebounced(isInViewport: boolean): void {
    if (this.hasTrackedView || this.hidden) return;

    clearTimeout(this.trackingDebounceTimer);
    this.trackingDebounceTimer = window.setTimeout(() => {
      if (this.hasTrackedView || !isInViewport || this.hidden) return;
      this.trackingObserver?.unobserve(this);
      this.hasTrackedView = true;
      trackBrandlistListView(this.brand, this.position, this.totalBrands, this.loggedIn);
    }, 300);
  }

  private handleClick = () => {
    trackBrandlistListClick(this.brand, this.position, this.totalBrands, this.loggedIn);
  };

  private formatLogoAlt(brandName: string): string {
    const template = this.messages['ents_wishlist_brands_logo_alt'] ?? '{0}';
    return template.replace('{0}', brandName);
  }

  render() {
    return html`
      <a
        class="ents-wishlist-brand-tile__link"
        href="${this.wishlistAppConfig.brandsUrl}${this.brand.niceName}/"
        data-test-ents-wishlist-brand-tile-link="${this.brand.code}"
        @click="${this.handleClick}"
      >
        ${
          this.brand.logo
            ? html`<img
                class="ents-wishlist-brand-tile__logo"
                src="${this.brand.logo}"
                alt="${this.formatLogoAlt(this.brand.name)}"
                loading="lazy"
              />`
            : html`<span class="ents-wishlist-brand-tile__name">${this.brand.name}</span>`
        }
      </a>
      <ents-wishlist-brand-remove-button .brand="${this.brand}"></ents-wishlist-brand-remove-button>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ents-wishlist-brand-tile': WishlistBrandTileElement;
  }
}
